import * as React from "react";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";

// Component to listen for toast events outside of React components
function ToastListener() {
  const { toast } = useToast();
  
  React.useEffect(() => {
    const handleToastEvent = (e: Event) => {
      const event = e as CustomEvent;
      if (event.detail) {
        toast(event.detail);
      }
    };
    
    document.addEventListener("toast", handleToastEvent);
    return () => {
      document.removeEventListener("toast", handleToastEvent);
    };
  }, [toast]);
  
  return null;
}

// The component that renders all toasts
function ToastList() {
  const { toasts, dismiss } = useToast();
  
  return (
    <AnimatePresence>
      {toasts.map(({ id, title, description, action, variant = "default", ...props }) => {
        const Icon = variant === "destructive" 
          ? AlertTriangle 
          : variant === "success"
          ? CheckCircle2
          : Info;

        return (
          <motion.div
            key={id}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="pointer-events-auto"
          >
            <Toast variant={variant} {...props}>
              <div className="flex w-full gap-3">
                <Icon className="h-5 w-5 shrink-0" />
                <div className="flex-1 grid gap-1">
                  {title && <ToastTitle>{title}</ToastTitle>}
                  {description && (
                    <ToastDescription>{description}</ToastDescription>
                  )}
                </div>
                <ToastClose onClick={() => dismiss(id)} />
              </div>
              {action}
            </Toast>
          </motion.div>
        );
      })}
    </AnimatePresence>
  );
}

export function Toaster() {
  return (
    <ToastProvider>
      <ToastList />
      <ToastListener />
      <ToastViewport />
    </ToastProvider>
  );
}
